import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { userService } from "@/entities/user/api/userService";
import { resolveUserFullName } from "@/shared/lib/userName";
import { queryKeys } from "@/shared/lib/queryKeys";

const STALE_TIME = 5 * 60 * 1000;

type MaybeUsername = string | null | undefined;

/**
 * Traduz usernames em "Nome Apelido" para apresentação.
 *
 * Os DTOs de obra (trabalhadores, diários, registos de ponto) só trazem o
 * username; o nome vem do utilizador. As respostas ficam em cache partilhada
 * com `queryKeys.users.detail`, pelo que o mesmo utilizador só é pedido uma vez.
 *
 * Devolve um mapa username -> nome completo, com fallback para o próprio
 * username enquanto o perfil não chega (ou se não existir).
 */
export function useUserDisplayNames(usernames: MaybeUsername[]): Record<string, string> {
    const key = usernames.filter(Boolean).join("|");
    const unique = useMemo(
        () => Array.from(new Set(key ? key.split("|") : [])),
        [key]
    );

    const profileQueries = useQueries({
        queries: unique.map((username) => ({
            queryKey: queryKeys.users.detail(username),
            staleTime: STALE_TIME,
            queryFn: () => userService.getByUsername(username).catch(() => null),
        })),
    });

    return useMemo(
        () => Object.fromEntries(
            unique.map((username, index) => [
                username,
                resolveUserFullName(profileQueries[index]?.data, username),
            ])
        ),
        [unique, profileQueries]
    );
}

/** Versão para um único utilizador; devolve o username enquanto carrega. */
export function useUserDisplayName(username: MaybeUsername): string {
    const names = useUserDisplayNames([username]);
    return username ? names[username] ?? username : "";
}
